import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import type {
    KanbanReportActivityItemDto,
    KanbanStatusSummaryDto,
} from "@project/data/dto/kanban-reports.dto";
import { ProjectKanbanReportsRepository } from "@project/data/repositories/project-kanban-reports.repository";
import type {
    KanbanReportActivityItem,
    KanbanReports,
    KanbanStatusSummary,
} from "@project/domain/models/kanban-reports.model";
import { toAssigneeBuckets } from "@project/domain/utils/activity-responsible.util";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";

@injectable()
export class GetKanbanReportsUseCase {
    constructor(
        @inject(ProjectKanbanReportsRepository)
        private readonly repository: ProjectKanbanReportsRepository,
    ) {}

    async execute(projectId: number): Promise<KanbanReports> {
        const [statusSummaryRes, pendingRes] = await Promise.all([
            this.repository.getStatusSummary(projectId),
            this.repository.getPending(projectId),
        ]);

        if (statusSummaryRes.isLeft()) {
            ToastManagerStore.error(statusSummaryRes.getLeftOrThrow().message);
        }
        if (pendingRes.isLeft()) {
            ToastManagerStore.error(pendingRes.getLeftOrThrow().message);
        }

        const statusSummaryDto: KanbanStatusSummaryDto =
            statusSummaryRes.getOrElse({
                total: 0,
                by_status: {},
            });
        const pendingDto: KanbanReportActivityItemDto[] = pendingRes.getOrElse(
            [] as KanbanReportActivityItemDto[],
        );

        const statusSummary: KanbanStatusSummary = {
            total: statusSummaryDto.total ?? 0,
            byStatus: statusSummaryDto.by_status ?? {},
        };

        const parseDeadline = (
            deadlineIso: string | null | undefined,
        ): DateTime | null => {
            if (!deadlineIso) return null;
            const parsed = DateTime.fromISO(deadlineIso);
            return parsed.isValid ? parsed : null;
        };

        const mapItem = (
            dto: KanbanReportActivityItemDto,
        ): KanbanReportActivityItem => ({
            id: dto.id,
            name: dto.name,
            status: dto.status,
            priority: dto.priority ?? null,
            deadline: parseDeadline(dto.deadline),
            assignedEmployeeId: dto.assigned_employee ?? null,
            teamEmployeeIds: dto.team_employee_ids ?? [],
        });

        const pending: KanbanReportActivityItem[] = pendingDto.map(mapItem);
        const pendingByAssignee = toAssigneeBuckets(pending);

        return {
            statusSummary,
            pending,
            pendingByAssignee,
            pendingByTeam: [],
        };
    }
}
